import { useEffect, useState } from 'react'
import { fetchWithAuth } from '../../utils/api'
import CombinedBoundaryEditor from '../CombinedBoundaryEditor'

interface SiteItem { id: number; name: string; geometry_json?: any; polygon_coords?: number[][] | null; allowed_users?: string[]; created_at?: string }
interface AreaItem { id?: number; name: string; geometry_json?: any; polygon_coords?: number[][] | null; created_at?: string }

function SiteManagement() {
  const [sites, setSites] = useState<SiteItem[]>([])
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null)
  const [site, setSite] = useState<any>(null)
  const [areas, setAreas] = useState<AreaItem[]>([])
  // unified editor state
  const [showCombinedEditor, setShowCombinedEditor] = useState(false)
  const [combinedSiteMulti, setCombinedSiteMulti] = useState<number[][][] | null>(null)
  const [combinedAreas, setCombinedAreas] = useState<{ id?: number; name: string; multi: number[][][] }[] | null>(null)
  const [initialEditorMode, setInitialEditorMode] = useState<'site' | 'area'>('site')
  const [initialEditorAreaIdx, setInitialEditorAreaIdx] = useState<number>(0)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Site details form state
  const [showSiteForm, setShowSiteForm] = useState(false)
  const [editingSiteDetails, setEditingSiteDetails] = useState<SiteItem | null>(null)
  const [siteForm, setSiteForm] = useState({ name: '', allowed_users: '' })
  
  // Area details form
  const [showAreaForm, setShowAreaForm] = useState(false)
  const [editingAreaDetailsIndex, setEditingAreaDetailsIndex] = useState<number | null>(null)
  const [areaForm, setAreaForm] = useState({ name: '' })

  useEffect(() => { loadSites() }, [])

  const loadSites = async () => {
    const res = await fetchWithAuth('/api/admin/sites')
    if (res.ok) {
      const data = await res.json()
      setSites(data)
    }
  }

  // Load full bundle on demand (when opening boundaries or areas modal)

  const loadFull = async (id: number) => {
    setStatus(null)
    const res = await fetchWithAuth(`/api/admin/sites/${id}/full`)
    if (res.ok) {
      const data = await res.json()
      setSite(data.site)
      setAreas(data.areas)
      return data as { site: any; areas: AreaItem[] }
    }
    return null
  }

  const toMultiFromGeo = (geo: any | null | undefined): number[][][] => {
    if (!geo) return []
    if (geo.type === 'MultiPolygon') {
      // GeoJSON uses [lng,lat]; convert to [lat,lng] and take first ring
      return geo.coordinates.map((poly: any) => poly[0].map((p: any) => [p[1], p[0]]))
    }
    return []
  }

  const toMultiFromLegacy = (coords: number[][] | null | undefined): number[][][] => {
    if (!coords || !Array.isArray(coords) || coords.length < 3) return []
    return [coords]
  }

  const toGeoFromMulti = (multi: number[][][]): any => ({
    type: 'MultiPolygon',
    coordinates: multi.map(poly => [poly.map(([lat,lng]) => [lng,lat]).concat([[poly[0][1], poly[0][0]]])])
  })

  const openSiteEditor = async (id: number) => {
    setSelectedSiteId(id)
    const bundle = await loadFull(id)
    const siteMulti = bundle?.site?.geometry_json
      ? toMultiFromGeo(bundle.site.geometry_json)
      : toMultiFromLegacy(bundle?.site?.polygon_coords)
    const areasWorking = (bundle?.areas || []).map(a => ({
      id: a.id,
      name: a.name,
      multi: a.geometry_json ? toMultiFromGeo(a.geometry_json) : toMultiFromLegacy(a.polygon_coords as any)
    }))
    setCombinedSiteMulti(siteMulti)
    setCombinedAreas(areasWorking)
    setShowCombinedEditor(true)
  }

  // area boundary editing goes through the combined editor
  const openAreaEditor = async (idx: number) => {
    if (!selectedSiteId) return
    const bundle = await loadFull(selectedSiteId)
    const siteMulti = bundle?.site?.geometry_json
      ? toMultiFromGeo(bundle.site.geometry_json)
      : toMultiFromLegacy(bundle?.site?.polygon_coords)
    const areasWorking = (bundle?.areas || []).map(a => ({
      id: a.id,
      name: a.name,
      multi: a.geometry_json ? toMultiFromGeo(a.geometry_json) : toMultiFromLegacy(a.polygon_coords as any)
    }))
    setCombinedSiteMulti(siteMulti)
    setCombinedAreas(areasWorking)
    setInitialEditorMode('area')
    setInitialEditorAreaIdx(idx)
    setShowCombinedEditor(true)
  }

  const openAreaDetails = (idx: number | null) => {
    setEditingAreaDetailsIndex(idx)
    if (idx == null) {
      setAreaForm({ name: 'New Area' })
    } else {
      setAreaForm({ name: areas[idx].name })
    }
    setShowAreaForm(true)
  }

  const submitAreaDetails = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null); setStatus(null)
    if (!selectedSiteId) return
    try {
      if (editingAreaDetailsIndex == null) {
        const res = await fetchWithAuth('/api/admin/areas', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ site_id: selectedSiteId, name: areaForm.name })
        })
        if (!res.ok) throw new Error('Failed to create area')
        setStatus('Area created')
      } else {
        const a = areas[editingAreaDetailsIndex]
        if (!a.id) return
        const res = await fetchWithAuth(`/api/admin/areas/${a.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: areaForm.name })
        })
        if (!res.ok) throw new Error('Failed to update area')
        setStatus('Area updated')
      }
      setShowAreaForm(false)
      loadFull(selectedSiteId)
    } catch (err: any) {
      setError(err.message || 'Failed to save area')
    }
  }

  const deleteArea = async (id?: number) => {
    if (!id) return
    if (!confirm('Delete this area?')) return
    const res = await fetchWithAuth(`/api/admin/areas/${id}`, { method: 'DELETE' })
    if (res.ok && selectedSiteId) { setStatus('Area deleted'); loadFull(selectedSiteId) }
  }

  // area geometry is saved via the combined editor only

  // const addArea = () => setAreas([...areas, { name: 'New Area', geometry_json: null }])

  // Helpers for site form
  const openCreateSite = () => {
    setEditingSiteDetails(null)
    setSiteForm({ name: '', allowed_users: '' })
    setShowSiteForm(true)
  }

  const openEditSite = (s: SiteItem) => {
    setEditingSiteDetails(s)
    const allowed = (s.allowed_users || []).join('\n')
    setSiteForm({ name: s.name, allowed_users: allowed })
    setShowSiteForm(true)
  }

  const submitSiteForm = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null); setStatus(null)
    const allowed_users = siteForm.allowed_users
      .split('\n')
      .map(x => x.trim())
      .filter(Boolean)
    if (!siteForm.name || allowed_users.length === 0) {
      setError('Name and at least one allowed user email are required')
      return
    }
    try {
      if (editingSiteDetails) {
        const res = await fetchWithAuth(`/api/admin/sites/${editingSiteDetails.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: siteForm.name, allowed_users })
        })
        if (!res.ok) throw new Error('Failed to update site')
        setStatus('Site updated')
      } else {
        const res = await fetchWithAuth('/api/admin/sites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: siteForm.name, allowed_users })
        })
        if (!res.ok) throw new Error('Failed to create site')
        const created = await res.json()
        setSelectedSiteId(created.id)
        setStatus('Site created')
      }
      setShowSiteForm(false)
      loadSites()
    } catch (err: any) {
      setError(err.message || 'Failed to save site')
    }
  }

  const deleteSite = async (id: number) => {
    if (!confirm('Delete this site? This will also delete its areas and map links.')) return
    const res = await fetchWithAuth(`/api/admin/sites/${id}`, { method: 'DELETE' })
    if (res.ok) {
      setStatus('Site deleted')
      if (selectedSiteId === id) setSelectedSiteId(null)
      loadSites()
    }
  }

  // Areas modal control
  const [showAreasModal, setShowAreasModal] = useState(false)
  const closeAreasModal = () => { setShowAreasModal(false) }

  return (
    <div>
      <h2>Site Management</h2>
      {status && <div className="success">{status}</div>}
      {error && <div className="error">{error}</div>}
      <div className="table-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <div>Sites</div>
          <button className="btn btn-success" onClick={openCreateSite}>➕ Add New Site</button>
        </div>
        {sites.length === 0 ? (
          <div className="loading">No sites found</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Users</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sites.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{(s.allowed_users || []).length}</td>
                  <td className="date-cell">{s.created_at ? new Date(s.created_at).toISOString().slice(0,19).replace('T',' ') : '-'}</td>
                  <td>
                    <div className="task-actions">
                      <button className="btn" onClick={() => openEditSite(s)}>Edit</button>
                      <button className="btn" onClick={() => openSiteEditor(s.id)}>Edit map</button>
                      <button className="btn btn-danger" onClick={() => deleteSite(s.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* unified editor below */}

      {showSiteForm && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{editingSiteDetails ? 'Edit Site' : 'Add New Site'}</h3>
              <button className="close-button" onClick={() => setShowSiteForm(false)}>×</button>
            </div>
            {error && <div className="error">{error}</div>}
            <form onSubmit={submitSiteForm} className="request-form">
              <div className="form-group">
                <label>Site Name *</label>
                <input type="text" value={siteForm.name} onChange={e => setSiteForm({ ...siteForm, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Allowed Users (one email per line) *</label>
                <textarea value={siteForm.allowed_users} onChange={e => setSiteForm({ ...siteForm, allowed_users: e.target.value })} placeholder={'user1@example.com\nuser2@example.com'} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSiteForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAreaForm && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3>{editingAreaDetailsIndex == null ? 'Add New Area' : 'Edit Area'}</h3>
              <button className="close-button" onClick={() => setShowAreaForm(false)}>×</button>
            </div>
            {error && <div className="error">{error}</div>}
            <form onSubmit={submitAreaDetails} className="request-form">
              <div className="form-group">
                <label>Area Name *</label>
                <input type="text" value={areaForm.name} onChange={e => setAreaForm({ name: e.target.value })} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAreaForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAreasModal && (
        <div className="modal-overlay">
          <div className="modal-container" style={{ width: 'min(900px, 96vw)' }}>
            <div className="modal-header">
              <h3>Areas for {site?.name || ''}</h3>
              <button className="close-button" onClick={closeAreasModal}>×</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div>Manage areas. Use Boundaries to draw MultiPolygons.</div>
              <button className="btn" onClick={() => openAreaDetails(null)}>➕ New Area</button>
            </div>
            <div className="table-container">
              {areas.length === 0 ? (
                <div className="loading">No areas yet</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Boundary</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areas.map((a, idx) => (
                      <tr key={a.id || idx}>
                        <td>{a.name}</td>
                        <td>{Array.isArray(a.geometry_json?.coordinates) ? '✓ MultiPolygon' : '—'}</td>
                        <td className="date-cell">{a.created_at ? new Date(a.created_at).toISOString().slice(0,19).replace('T',' ') : '-'}</td>
                        <td>
                          <div className="task-actions">
                            <button className="btn" onClick={() => openAreaEditor(idx)}>Boundaries</button>
                            <button className="btn" onClick={() => openAreaDetails(idx)}>Edit</button>
                            <button className="btn btn-danger" onClick={() => deleteArea(a.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showCombinedEditor && combinedSiteMulti && combinedAreas && selectedSiteId && (
        <CombinedBoundaryEditor
          siteName={site?.name || ''}
          siteMulti={combinedSiteMulti}
          areas={combinedAreas}
          initialMode={initialEditorMode}
          initialAreaIndex={initialEditorAreaIdx}
          onSave={async (siteMulti, areasWorking, deletedAreaIds) => {
            try {
              const payload = {
                site: { name: site?.name, geometry_json: toGeoFromMulti(siteMulti) },
                areas: areasWorking.map(a => ({ id: a.id, name: a.name, geometry_json: toGeoFromMulti(a.multi) })),
                deleted_area_ids: Array.isArray(deletedAreaIds) ? deletedAreaIds : []
              }
              const res = await fetchWithAuth(`/api/admin/sites/${selectedSiteId}/full`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              })
              if (!res.ok) throw new Error('Failed to save')
              setStatus('Site and areas saved')
              const updated = await loadFull(selectedSiteId)
              if (updated) {
                const siteMultiUpdated = updated.site.geometry_json
                  ? toMultiFromGeo(updated.site.geometry_json)
                  : toMultiFromLegacy(updated.site.polygon_coords)
                const areasUpdated = updated.areas.map(a => ({
                  id: a.id,
                  name: a.name,
                  multi: a.geometry_json ? toMultiFromGeo(a.geometry_json) : toMultiFromLegacy(a.polygon_coords as any)
                }))
                // return updated data so the child updates its working state with IDs
                return { siteMulti: siteMultiUpdated, areas: areasUpdated }
              }
            } catch (err: any) {
              setError(err.message || 'Failed to save')
            }
          }}
          onClose={() => setShowCombinedEditor(false)}
        />
      )}
    </div>
  )
}

export default SiteManagement
